/**
 * ClientListPanel - left master panel for the Goals page.
 * Scrollable, searchable list of all clients; clicking a row selects that
 * client for the goal tree in the right (detail) panel.
 */

import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Search } from 'lucide-react';
import { useClientList } from '../../hooks/useClients';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { Avatar, Input } from '../../components/ui';
import { media } from '../../styles/media';
import type { Client } from '../../types/clients';

export interface ClientListPanelProps {
  selectedClientId: string | null;
  onSelect: (client: Client) => void;
}

const PAGE_SIZE = 50;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const SearchWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing[3]};
  flex-shrink: 0;
`;

const ClientList = styled.ul`
  flex: 1;
  min-height: 200px;
  max-height: calc(100vh - 320px);
  overflow-y: auto;
  margin: 0;
  padding: 0 ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]};
  list-style: none;

  ${media.desktop} {
    max-height: calc(100vh - 360px);
  }
`;

const ClientRow = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  min-height: 44px;
  background: ${({ theme, $selected }) => ($selected ? theme.colors.status.info.bg : 'transparent')};

  &:hover {
    background: ${({ theme, $selected }) =>
      $selected ? theme.colors.status.info.bg : theme.colors.backgrounds.page};
  }
`;

const ClientInfo = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ClientName = styled.span<{ $selected: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme, $selected }) =>
    $selected ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  color: ${({ theme, $selected }) => ($selected ? theme.colors.primary.dark : theme.colors.text.primary)};
  overflow-wrap: anywhere;
`;

const ClientMeta = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
`;

const StatusMessage = styled.li`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.light};
  text-align: center;
`;

const LoadMoreButton = styled.button`
  display: block;
  width: calc(100% - ${({ theme }) => theme.spacing[4]});
  margin: ${({ theme }) => theme.spacing[2]} auto 0;
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px dashed ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: none;
  color: ${({ theme }) => theme.colors.primary.base};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  min-height: 44px;

  &:hover {
    background: ${({ theme }) => theme.colors.status.info.bg};
  }
`;

export function ClientListPanel({ selectedClientId, onSelect }: ClientListPanelProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 300);

  // Reset to page 1 whenever the search term changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, isFetching } = useClientList({
    search: debouncedSearch || undefined,
    limit: PAGE_SIZE,
    page,
  });

  // Accumulate pages client-side instead of re-fetching from page 1 on "Load more" —
  // each page is fetched (and cached) once via its own React Query key.
  const [clients, setClients] = useState<Client[]>([]);
  useEffect(() => {
    if (!data) return;
    setClients((prev) => (page === 1 ? data.data : [...prev, ...data.data]));
  }, [data, page]);

  const total = data?.meta?.total ?? clients.length;
  const canLoadMore = clients.length < total;

  return (
    <Panel>
      <SearchWrapper>
        <Input
          type="search"
          value={search}
          placeholder="Search clients..."
          aria-label="Search clients"
          leftIcon={<Search />}
          fullWidth
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchWrapper>

      <ClientList aria-label="Clients">
        {isLoading && page === 1 ? (
          <StatusMessage>Loading clients...</StatusMessage>
        ) : clients.length === 0 ? (
          <StatusMessage>No clients found.</StatusMessage>
        ) : (
          <>
            {clients.map((client) => {
              const selected = client.id === selectedClientId;
              return (
                <ClientRow
                  key={client.id}
                  $selected={selected}
                  onClick={() => onSelect(client)}
                  aria-current={selected}
                >
                  <Avatar firstName={client.firstName} lastName={client.lastName} size="sm" />
                  <ClientInfo>
                    <ClientName $selected={selected}>
                      {client.firstName} {client.lastName}
                    </ClientName>
                    {client.externalId && <ClientMeta>ID: {client.externalId}</ClientMeta>}
                  </ClientInfo>
                </ClientRow>
              );
            })}
            {canLoadMore && (
              <LoadMoreButton onClick={() => setPage((p) => p + 1)} disabled={isFetching}>
                {isFetching ? 'Loading...' : 'Load more'}
              </LoadMoreButton>
            )}
          </>
        )}
      </ClientList>
    </Panel>
  );
}

export default ClientListPanel;
